import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Mail, Lock, User, CheckCircle } from 'lucide-react';

interface InvitationData {
  id: string;
  sharing_operation_id: string;
  email: string;
  operation_name: string;
}

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creating, setCreating] = useState(false);

  const invitationCode = searchParams.get('code');

  useEffect(() => {
    if (!invitationCode) {
      toast.error('Code d\'invitation manquant');
      navigate('/');
      return;
    }

    loadInvitation();
  }, [invitationCode]);

  const loadInvitation = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('check_invitation_by_code', { p_invitation_code: invitationCode });

      if (error || !data || data.length === 0 || !data[0].id) {
        throw new Error('Invitation invalide ou expirée');
      }

      const inv = data[0];
      if (inv.already_accepted) {
        toast.error('Cette invitation a déjà été utilisée');
        navigate('/');
        return;
      }

      setInvitation({
        id: inv.id,
        sharing_operation_id: '',
        email: inv.email,
        operation_name: inv.operation_name || '',
      });
    } catch (error: any) {
      console.error('Erreur chargement invitation:', error);
      toast.error('Invitation introuvable ou expirée.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (!invitation) return;

    setCreating(true);
    const toastId = toast.loading('Création de votre compte...');

    try {
      // Créer le compte utilisateur
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password: password,
        options: {
          data: {
            role: 'admin',
          },
        },
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error('Erreur lors de la création du compte');
      }

      // Mettre à jour l'invitation via edge function (service role, pas de RLS)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      let accessToken = authData.session?.access_token;
      if (!accessToken) {
        const { data: signInData } = await supabase.auth.signInWithPassword({
          email: invitation.email,
          password,
        });
        accessToken = signInData?.session?.access_token;
      }
      const response = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'accept_manager_invitation',
          invitation_code: invitationCode,
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur mise à jour invitation');

      toast.success('Compte créé ! Votre accès sera activé après validation par l\'administrateur.', { id: toastId });

      navigate('/');
    } catch (error: any) {
      console.error('Erreur création compte:', error);
      toast.error('Impossible de créer le compte. Vérifiez votre lien d\'invitation ou contactez-nous.', { id: toastId });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F8BD3D]"></div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#F8BD3D] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-neutral-900" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Bienvenue !
          </h1>
          <p className="text-neutral-600">
            Vous avez été invité à rejoindre
          </p>
          <p className="text-lg font-semibold text-[#F8BD3D] mt-1">
            {invitation.operation_name}
          </p>
        </div>

        <form onSubmit={handleAcceptInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email
            </label>
            <input
              type="email"
              value={invitation.email}
              disabled
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <Lock className="w-4 h-4 inline mr-2" />
              Mot de passe *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 caractères"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#F8BD3D] focus:border-transparent"
              minLength={8}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <Lock className="w-4 h-4 inline mr-2" />
              Confirmer le mot de passe *
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmer le mot de passe"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#F8BD3D] focus:border-transparent"
              minLength={8}
              required
            />
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full px-4 py-3 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Création en cours...' : 'Créer mon compte'}
          </button>
        </form>

        <p className="text-xs text-neutral-500 text-center mt-6">
          Votre compte sera activé après validation par l'administrateur Sun Is Up.
        </p>
      </div>
    </div>
  );
}
